import type { TagGroup } from '@vision/ui/interfaces';
import type { AccountTag } from '@vision/ui/interfaces/account-tag';
import { useLazyFetchTagWithPaginationQuery, useLazyGetTagGroupsQuery } from '@vision/ui/services/tag.service';
import { collectSelectedTags } from '@vision/ui/services/tag.service.pagination';
import { useEffect, useState } from 'react';

type Args = { nodeId: string; tagIds?: string[]; tagGroupIds?: string[]; includeInternal?: boolean; skip?: boolean };
type Result = { tags: AccountTag[]; groups: TagGroup[]; isLoading: boolean; isError: boolean };

export function useTagSelection({
  nodeId,
  tagIds = [],
  tagGroupIds = [],
  includeInternal,
  skip = false,
}: Args): Result {
  const [getTags] = useLazyFetchTagWithPaginationQuery();
  const [getGroups] = useLazyGetTagGroupsQuery();
  const tagKey = [...new Set(tagIds)].sort().join(',');
  const groupKey = [...new Set(tagGroupIds)].sort().join(',');
  const key = JSON.stringify([nodeId, tagKey, groupKey, includeInternal, skip]);
  const [state, setState] = useState<Result & { key: string }>();

  useEffect(() => {
    let cancelled = false;
    const empty: Result & { key: string } = { key, tags: [], groups: [], isLoading: false, isError: false };
    if (skip || (!tagKey && !groupKey)) {
      setState(empty);
      return;
    }
    setState({ ...empty, isLoading: true });

    const load = async () => {
      try {
        const [tags, groups] = await Promise.all([
          collectSelectedTags(
            tagKey ? tagKey.split(',') : [],
            (ids, page) => getTags({ nodeId, ids, page, perPage: 50, includeInternal }, true).unwrap(),
            () => cancelled,
          ),
          collectSelectedTags(
            groupKey ? groupKey.split(',') : [],
            (ids, page) => getGroups({ nodeId, ids, page, perPage: 50, includeInternal: true }, true).unwrap(),
            () => cancelled,
          ),
        ]);
        if (!cancelled) setState({ ...empty, tags, groups });
      } catch {
        if (!cancelled) setState({ ...empty, isError: true });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [nodeId, tagKey, groupKey, includeInternal, skip, key, getTags, getGroups]);

  return state?.key === key
    ? state
    : { tags: [], groups: [], isLoading: !skip && !!(tagKey || groupKey), isError: false };
}
